import type PlatformService from '@debatdirect/ui-react/src/services/PlatformService';

import { container, injectable } from '../modules/container';
import env from '../env';

export type SearchParams = {
  q?: string;
  van?: string;
  tot?: string;
  categorie?: string[];
  partij?: string[];
  politici?: string[];
  dossier?: string[];
  status?: string[];
  sortering?: string;
  locatie?: string[];
  vanaf?: number;
};

type Bucket = {
  key: string;
  doc_count: number;
};

type Aggregation = {
  doc_count_error_upper_bound?: number;
  sum_other_doc_count?: number;
  buckets: Bucket[];
};

type Hit = {
  _id: string;
  _index: string;
  _score: number | null;
  _source: unknown; // I.E. AppDebate
  sort: number[];
};

type SearchResponse = {
  hits: {
    hits: Hit[];
    max_score: number | null;
    total: { value: number };
  };
  aggregations: Record<string, Aggregation>;
};

export type SearchResults = {
  hits: {
    hits: Hit[];
    max_score: number | null;
    total: { value: number };
  };
  hasMore: boolean;
  aggregations: Record<string, Aggregation>;
};

type SuggestionsResponse = {
  text: string;
  _id: string;
  _index: string;
  _score: number | null;
  _source: unknown; // I.E. AppDebate
}[];

@injectable()
export default class SearchService {
  private url: string;

  private pageSize = 0;

  constructor() {
    this.url = env.APP_ELASTIC_URL;
  }

  async search(params: URLSearchParams, value = '', page: number = 1): Promise<SearchResults> {
    const platform = container.get<PlatformService>('platform').isCapacitor() ? 'app' : 'web'; // @TODO: PlatformService (or core deps) needs a fix
    const queryString = `appVersion=${encodeURIComponent(env.APP_VERSION)}&platform=${encodeURIComponent(platform)}&totalFormat=new`;
    const from = page ? (page - 1) * this.pageSize : 0;

    const urlWithParams = `${this.url}?${params.toString()}&vanaf=${from.toString()}&${queryString}`;

    const response = await fetch(urlWithParams, { method: 'GET' });
    const data = (await response.json()) as SearchResponse;

    // Remember the page size for next pages
    this.pageSize = page === 1 ? data.hits.hits.length : this.pageSize;

    if (value) {
      window._paq?.push(['trackSiteSearch', value, false, data.hits.total.value]);
    }

    return {
      hits: data.hits,
      hasMore: data.hits.total.value > from + data.hits.hits.length,
      aggregations: data.aggregations,
    };
  }

  async getSuggestions(query: string): Promise<string[]> {
    const response = await fetch(`${this.url}?q=${encodeURIComponent(query)}&suggesties=1`, { method: 'GET' });
    const suggestionsResponse = (await response.json()) as SuggestionsResponse;

    return suggestionsResponse.map((suggestion) => suggestion.text);
  }
}
